import { useState, useRef, useEffect } from "react";
import { collection, doc, setDoc, getDoc, getDocs, addDoc, query, orderBy, limit, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { MATERIALS, BASELINE_Q, OPP_DISPLAY, resolveOpportunity } from "../constants/domain";
import { sum, pct } from "../utils/format";
import { C } from "../constants/colors";

// Owns the full org profile / audit domain: org, hauler, baseline, locations, invoice
// result, action plan, and the derived aggregates every screen reads. Also folds
// wasteData/wasteDataConfig (owned by useWasteData) into the same load/save/debounce
// so persistence stays byte-identical to the original single combined Firestore write.
export function useOrgProfile({user, setScreen, wasteData, setWasteData, wasteDataConfig, setWasteDataConfig, wasteAccounts, setWasteAccounts}){
  const [org,setOrg]=useState({name:"",industry:"",size:""});
  const [hauler,setHauler]=useState({});
  const [baseline,setBaseline]=useState({});
  const [auditData,setAuditData]=useState({});      // kept for legacy compat — unused when locationAudits active
  const [auditPhotos,setAuditPhotos]=useState([]);   // kept for legacy compat
  // Multi-location audit state
  // Each location: {id, name, auditData:{materialId:lbs}, photos:[{url,label,name,date}], customStreams:[{id,label,divertible,hint}]}
  const [locationAudits,setLocationAudits]=useState([]);
  const [activeLocationId,setActiveLocationId]=useState(null); // which location is being edited
  const [actionPlan,setActionPlan]=useState(null);
  const [userConstraints,setUserConstraints]=useState({timeline:"6 months",budget:"moderate",priority:"Cost Reduction"});

  // ── Firestore persistence ──────────────────────────────────
  const [orgId,setOrgId]=useState(null);
  const [orgAudits,setOrgAudits]=useState([]);
  const [appLoading,setAppLoading]=useState(false);
  const [obName,setObName]=useState("");
  const [obIndustry,setObIndustry]=useState("");
  const [obSize,setObSize]=useState("");
  const [obLoading,setObLoading]=useState(false);
  const debounceRef=useRef(null);

  const fmtDate=ts=>{
    if(!ts)return"";
    const d=ts.toDate?ts.toDate():new Date(ts);
    return d.toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})+" "+d.toLocaleTimeString("en-US",{hour:"2-digit",minute:"2-digit"});
  };

  const loadOrgAudits=async oid=>{
    try{
      const q=query(collection(db,"organizations",oid,"audits"),orderBy("createdAt","desc"),limit(5));
      const snap=await getDocs(q);
      setOrgAudits(snap.docs.map(d=>({id:d.id,...d.data()})));
    }catch(e){console.error("loadOrgAudits",e);}
  };

  const loadOrgProfile=async oid=>{
    try{
      const snap=await getDoc(doc(db,"organizations",oid));
      if(snap.exists()){
        const d=snap.data();
        setOrg({name:d.name||"",industry:d.industry||"",size:d.size||""});
        setHauler(d.hauler||{});
        setBaseline(d.baseline||{});
        if(d.locationAudits?.length) setLocationAudits(d.locationAudits);
        if(d.activeLocationId) setActiveLocationId(d.activeLocationId);
        if(d.userConstraints) setUserConstraints(d.userConstraints);
        if(d.wasteData?.length) setWasteData(d.wasteData);
        if(d.wasteDataConfig) setWasteDataConfig(d.wasteDataConfig);
        if(d.wasteAccounts?.length) setWasteAccounts(d.wasteAccounts);
      }
    }catch(e){console.error("loadOrgProfile",e);}
  };

  const saveOrgProfile=async()=>{
    if(!orgId)return;
    try{
      await setDoc(doc(db,"organizations",orgId),{
        name:org.name,industry:org.industry,size:org.size,
        hauler,baseline,
        locationAudits:sanitizeLocations(locationAudits),
        activeLocationId:activeLocationId||null,
        userConstraints,
        wasteData,wasteDataConfig,wasteAccounts,
      },{merge:true});
    }catch(e){console.error("saveOrgProfile",e);}
  };

  const initOrg=async uid=>{
    setAppLoading(true);
    try{
      const userSnap=await getDoc(doc(db,"users",uid));
      if(userSnap.exists()&&userSnap.data().orgId){
        const oid=userSnap.data().orgId;
        setOrgId(oid);
        await loadOrgProfile(oid);
        await loadOrgAudits(oid);
        setScreen("dashboard");
      }else{
        setScreen("org-onboarding");
      }
    }catch(e){console.error("initOrg",e);setScreen("org-onboarding");}
    setAppLoading(false);
  };

  const createOrg=async()=>{
    if(!obName||!obIndustry||!obSize)return;
    setObLoading(true);
    try{
      const orgRef=doc(collection(db,"organizations"));
      await setDoc(orgRef,{name:obName,industry:obIndustry,size:obSize,hauler:{},baseline:{},createdAt:serverTimestamp()});
      await setDoc(doc(db,"users",user.uid),{orgId:orgRef.id},{merge:true});
      setOrgId(orgRef.id);
      setOrg({name:obName,industry:obIndustry,size:obSize});
      setScreen("baseline");
    }catch(e){console.error("createOrg",e);setObLoading(false);}
  };

  // photos are excluded — data URIs can be several MB each, exceeding Firestore's 1 MB doc limit
  const sanitizeLocations=locs=>locs.map(({id,name,auditData,customStreams})=>({id,name,auditData:auditData||{},customStreams:customStreams||[]}));

  const saveNewAudit=async(overrides={})=>{
    if(!orgId)return;
    try{
      await addDoc(collection(db,"organizations",orgId,"audits"),{
        locationAudits:sanitizeLocations(locationAudits),
        locationNames:locationAudits.map(l=>l.name),
        userConstraints,actionPlan:null,
        ...overrides,
        createdAt:serverTimestamp(),updatedAt:serverTimestamp(),
      });
      loadOrgAudits(orgId);
    }catch(e){console.error("saveNewAudit",e);}
  };

  const loadAuditFromHistory=a=>{
    setLocationAudits(a.locationAudits||[]);
    setActionPlan(a.actionPlan||null);
    setUserConstraints(a.userConstraints||{timeline:"6 months",budget:"moderate",priority:"Cost Reduction"});
    setScreen("dashboard");
  };

  useEffect(()=>{
    if(user){setAppLoading(true);initOrg(user.uid);}
    else if(user===null){setOrgId(null);setOrgAudits([]);setAppLoading(false);}
  },[user?.uid]);

  useEffect(()=>{
    if(!orgId)return;
    clearTimeout(debounceRef.current);
    debounceRef.current=setTimeout(()=>saveOrgProfile(),2000);
    return()=>clearTimeout(debounceRef.current);
  },[org,hauler,baseline,locationAudits,activeLocationId,userConstraints,wasteData,wasteDataConfig,wasteAccounts,orgId]);

  // ── Location audit helpers ─────────────────────────────────
  const newLocationId=()=>"loc_"+Date.now();

  const addLocation=(name="")=>{
    const id=newLocationId();
    const loc={id,name:name||`Location ${locationAudits.length+1}`,auditData:{},photos:[],customStreams:[]};
    setLocationAudits(prev=>[...prev,loc]);
    return id;
  };

  const updateLocation=(id,patch)=>{
    setLocationAudits(prev=>prev.map(l=>l.id===id?{...l,...patch}:l));
  };

  const removeLocation=(id)=>{
    setLocationAudits(prev=>prev.filter(l=>l.id!==id));
    if(activeLocationId===id) setActiveLocationId(null);
  };

  const getActiveLoc=()=>locationAudits.find(l=>l.id===activeLocationId)||null;

  // Custom stream helpers (per-location)
  const addCustomStream=(locId)=>{
    const id="custom_"+Date.now();
    setLocationAudits(prev=>prev.map(l=>l.id===locId?{...l,customStreams:[...l.customStreams,{id,label:"",divertible:"none",hint:""}]}:l));
  };
  const updateCustomStream=(locId,streamId,field,val)=>{
    setLocationAudits(prev=>prev.map(l=>l.id===locId?{...l,customStreams:l.customStreams.map(s=>s.id===streamId?{...s,[field]:val}:s)}:l));
  };
  const removeCustomStream=(locId,streamId)=>{
    setLocationAudits(prev=>prev.map(l=>{
      if(l.id!==locId) return l;
      const ad={...l.auditData}; delete ad[streamId];
      return {...l,customStreams:l.customStreams.filter(s=>s.id!==streamId),auditData:ad};
    }));
  };

  const materials=MATERIALS[org.industry]||[];
  const baseQuestions=BASELINE_Q[org.industry]||[];

  // Aggregate audit data across all locations
  const aggregatedAuditData=locationAudits.reduce((acc,loc)=>{
    Object.entries(loc.auditData||{}).forEach(([k,v])=>{acc[k]=(acc[k]||0)+v;});
    return acc;
  },{});

  // Aggregate custom streams across all locations (deduplicate by label)
  const allCustomStreams=locationAudits.flatMap(loc=>(loc.customStreams||[]).filter(s=>s.label.trim()));

  const allMaterials=[...materials,...allCustomStreams];
  const totalWeight=sum(Object.values(aggregatedAuditData));
  const totalPhotos=locationAudits.reduce((a,l)=>a+(l.photos||[]).length,0);
  const hasAudit=totalWeight>0;
  const hasPlan=!!actionPlan&&!actionPlan.error;

  // Resolve opportunity tags per material (aggregated)
  const resolvedMaterials=allMaterials.map(m=>({
    ...m, weight:aggregatedAuditData[m.id]||0,
    resolved: resolveOpportunity(m.divertible, hauler, baseline),
  }));

  const topMaterials=[...resolvedMaterials].filter(m=>m.weight>0).sort((a,b)=>b.weight-a.weight).slice(0,5);
  const divertibleNow=sum(resolvedMaterials.filter(m=>m.resolved==="feasible_now").map(m=>m.weight));
  const divertibleVendor=sum(resolvedMaterials.filter(m=>m.resolved==="new_vendor").map(m=>m.weight));
  const diversionRateNow=pct(divertibleNow,totalWeight);
  const diversionRateTotal=pct(divertibleNow+divertibleVendor,totalWeight);

  // Build donut segments from resolved tags
  const byResolved={};
  resolvedMaterials.forEach(m=>{if(m.weight>0){byResolved[m.resolved]=(byResolved[m.resolved]||0)+m.weight;}});
  const segments=Object.entries(byResolved).map(([k,v])=>({
    label:OPP_DISPLAY[k]?.label||k,
    value:parseFloat(pct(v,totalWeight)),
    color:OPP_DISPLAY[k]?.color||C.muted,
  })).sort((a,b)=>b.value-a.value);

  return {
    org,setOrg,hauler,setHauler,baseline,setBaseline,
    locationAudits,setLocationAudits,activeLocationId,setActiveLocationId,
    actionPlan,setActionPlan,userConstraints,setUserConstraints,
    orgId,orgAudits,appLoading,
    obName,setObName,obIndustry,setObIndustry,obSize,setObSize,obLoading,
    fmtDate,createOrg,saveNewAudit,loadAuditFromHistory,
    addLocation,updateLocation,removeLocation,getActiveLoc,
    addCustomStream,updateCustomStream,removeCustomStream,
    materials,baseQuestions,
    aggregatedAuditData,allCustomStreams,allMaterials,
    totalWeight,totalPhotos,hasAudit,hasPlan,
    resolvedMaterials,topMaterials,divertibleNow,divertibleVendor,diversionRateNow,diversionRateTotal,
    segments,
  };
}
